import React from 'react';
import gql from 'graphql-tag';
import Moment from 'moment-timezone';
import { FormattedMessage } from 'react-intl';
import apollo from '../services/apollo';
import ArrayUtil from './ArrayUtil';
import { AssessmentConstants, AppConstants } from '../shared/Constants';

class AssessmentUtil {
  /**
   * generate list of respondent
   * @param {Object} assessment
   */
  static generate360RepondentsStringList(survey) {
    let stringList = '';
    if (survey) {
      if (survey.directReports) {
        stringList += AssessmentUtil.getRespondentListIdByKey(survey.directReports);
      }

      if (survey.others) {
        stringList += AssessmentUtil.getRespondentListIdByKey(survey.others);
      }

      if (survey.peers) {
        stringList += AssessmentUtil.getRespondentListIdByKey(survey.peers);
      }

      if (survey.managers) {
        stringList += AssessmentUtil.getRespondentListIdByKey(survey.managers);
      }
    }

    return stringList || null;
  }

  /**
   * get survey title
   * @param { Object } assessment
   * @return { string }
   */
  static getSurveyTitle(assessment) {
    const { survey } = assessment;
    const { title } = assessment.template;

    return (
      <span className="survey-title">
        {survey.name}
        {' '}
-
        {' '}
        <span className="text-capitalize">
          {survey.selfRespondent.firstName}
          {' '}
          {survey.selfRespondent.lastName}
          {' '}
-
          {' '}
          {title}
          {' '}
        </span>
      </span>
    );
  }

  static getRespondentListIdByKey(resList) {
    let stringList = '';
    resList.forEach(({ respondent }) => {
      stringList += respondent.id + AppConstants.UUID_STR_SEPARATOR;
    });
    return stringList;
  }

  static updateExpiration({ accountId, assessmentId, expirationTime }) {
    const UPDATE_EXP_QUERY = gql`
      mutation($input: UpdateExpirationTimeAssessmentInput!) {
        updateExpirationTimeAssessment(input: $input) {
          assessment {
            id
            status
            expiredOn
          }
        }
      }
    `;

    return apollo.mutate({
      mutation: UPDATE_EXP_QUERY,
      variables: {
        input: {
          accountId,
          assessmentId,
          expirationTime,
        },
      },
    });
  }

  /**
   * check the new expiration if it's before limitation time
   * @param {Date} newExpiration Date
   * @return: boolean
   */
  static isSameOrBeforeLimit(newExpiration) {
    const thisMoment = Moment(new Date());
    const newToMoment = Moment(newExpiration);
    if (thisMoment.isValid() && newToMoment.isValid()) {
      if (newToMoment.isSameOrBefore(thisMoment.add(AssessmentConstants.MAX_EXPANDS_TIME, 'days').endOf('day'))) {
        return true;
      }
    }
    return false;
  }

  /**
   * check if the new expiration if it's from today
   * @param {Date} newExpiration Date
   * @return: {boolean}
   */
  static isSameOrAfterNow(newExpiration) {
    const newToMoment = Moment(newExpiration);
    if (newToMoment.isValid()) {
      const nowToMoment = Moment().startOf('day');
      if (nowToMoment.isSameOrBefore(newToMoment)) {
        return true;
      }
    }
    return false;
  }

  /**
   * check if assessment is available to send notification or not
   * @param {Object} assessment
   * @return: boolean
   */
  static isNotifiable(assessment) {
    if (
      assessment.status === AssessmentConstants.ASMT_STATUS.NEW
      || assessment.status === AssessmentConstants.ASMT_STATUS.INVITED
      || assessment.status === AssessmentConstants.ASMT_STATUS.STARTED
    ) {
      return true;
    }
    return false;
  }

  /**
   * check if assessment is available to send reminding email or not
   * @param {Object} assessment
   * @return: boolean
   */
  static isRemindable(assessment) {
    if (
      assessment.status === AssessmentConstants.ASMT_STATUS.INVITED
      || assessment.status === AssessmentConstants.ASMT_STATUS.STARTED
    ) {
      return true;
    }
    return false;
  }

  /**
   * check if assessment is available to send inviting email or not
   * @param {Object} assessment
   * @return: boolean
   */
  static isInvitable(assessment) {
    if (assessment.status === AssessmentConstants.ASMT_STATUS.NEW) {
      return true;
    }
    return false;
  }

  static isResetAssessmentDeclineStatus(assessment) {
    if (assessment.status === AssessmentConstants.ASMT_STATUS.DECLINED) {
      return true;
    }
    return false;
  }

  /**
   * check if assessment is available to extend time limit
   * @param {Object} assessment
   * @return: boolean
   */
  static isTimeExtendable(assessment) {
    if (
      assessment.status === AssessmentConstants.ASMT_STATUS.NEW
      || assessment.status === AssessmentConstants.ASMT_STATUS.INVITED
      || assessment.status === AssessmentConstants.ASMT_STATUS.STARTED
      || assessment.status === AssessmentConstants.ASMT_STATUS.EXPIRED
    ) {
      return true;
    }
    return false;
  }

  static getSurveyStatus(statuses) {
    let totalAssessments = 0;
    Object.keys(AssessmentConstants.ASMT_STATUS).forEach((item) => {
      if (statuses[item]) {
        totalAssessments += parseInt(statuses[item], 10);
      }
    });
    const sumitted = statuses.SUBMITTED ? parseInt(statuses.SUBMITTED, 10) : 0;
    return {
      htmlString: `${sumitted} / ${totalAssessments}`,
      string: statuses.SUBMITTED === totalAssessments ? 'FINISH' : 'IN_PROGRESS',
    };
  }

  /**
   * generate preview invite/remind link
   * @param {Object} assessment
   * @return {String}
   */
  static generatePreviewLink(assessment) {
    if (assessment) {
      if (assessment.status === AssessmentConstants.ASMT_STATUS.NEW) {
        return (
          <FormattedMessage id="assessment.invite.preview" defaultMessage="Preview Invite">
            {text => (
              <a href={assessment.inviteURL} rel="noopener noreferrer" target="_blank">
                {text}
              </a>
            )}
          </FormattedMessage>
        );
      }
      if (
        assessment.status === AssessmentConstants.ASMT_STATUS.INVITED
        || assessment.status === AssessmentConstants.ASMT_STATUS.STARTED
      ) {
        return (
          <FormattedMessage id="assessment.remind.preview" defaultMessage="Preview Reminder">
            {text => (
              <a href={assessment.remindURL} rel="noopener noreferrer" target="_blank">
                {text}
              </a>
            )}
          </FormattedMessage>
        );
      }
    }
    return null;
  }

  static getAssessmentTemplateTextId(instrument) {
    switch (instrument) {
      case AssessmentConstants.INSTRUMENT_TYPES.JPS:
        return 'job_profile_survey';
      case AssessmentConstants.INSTRUMENT_TYPES.C360:
        return 'survey';
      case AssessmentConstants.INSTRUMENT_TYPES.STD:
        return 'assessment';
      default:
        return '';
    }
  }

  /**
   * add instrument to array of assessment
   * @param {Array} timeline
   * @return {Array}
   */
  static mapInstrumentToAssessment(timeline) {
    const proceed = timeline.map((item) => {
      let instrument = 'STD';
      if (item.__typename === 'SurveyRespondent') {
        instrument = 'C360';
      } else if (item.__typename === 'JobProfileSurvey') {
        instrument = 'JPS';
      }
      return { ...item, instrument };
    });

    return proceed;
  }

  /**
   * get standard assessment
   * @param {Array} timeline
   * @return {Array}
   */
  static getStandardAsmt(timeline) {
    const asmtList = ArrayUtil.isNotEmpty(timeline) ? timeline.filter(asmt => asmt.__typename === 'Assessment') : [];
    return asmtList;
  }
}

export class AbilityRangeGenerator {
  static generateRange(score, low, high) {
    const ranges = [];
    for (let i = AssessmentConstants.REPORT_DIMENSION.LOW; i <= AssessmentConstants.REPORT_DIMENSION.HIGH; i += 1) {
      if (score === i && (i >= low && i <= high)) {
        ranges.push(
          <li key={i} className="on is-selected">
            <span>{i}</span>
          </li>,
        );
      } else if (score === i) {
        const outRangeScore = AbilityRangeGenerator.generateOutRangeScore(score, { low, high });
        ranges.push(outRangeScore);
      } else if (i >= low && i <= high) {
        ranges.push(
          <li key={i} className="on">
            <span>{i}</span>
          </li>,
        );
      } else {
        ranges.push(
          <li key={i} className="">
            <span>{i}</span>
          </li>,
        );
      }
    }

    return ranges;
  }

  static generateOutRangeScore(score, dimensions) {
    let distance = 0;
    const { low, high } = dimensions;
    if (score < low) {
      distance = low - score;
    } else if (score > high) {
      distance = score - high;
    }

    if (distance > AssessmentConstants.REPORT_DIMENSION.OUT_RANGE) {
      return (
        <li key={score} className="is-selected is-out">
          <span>{score}</span>
        </li>
      );
    }
    return (
      <li key={score} className="is-selected">
        <span>{score}</span>
      </li>
    );
  }

  static generateRangeContainer(result) {
    const label = result.displayName;
    const list = (
      <ul className="range">
        {AbilityRangeGenerator.generateRange(result.score, result.benchmarkLow, result.benchmarkHigh)}
      </ul>
    );
    return (
      <div key={result.code} className="ability">
        <div className="ability-label">{label}</div>
        <div className="ability-body">{list}</div>
      </div>
    );
  }

  static generateInterestListForRespondent(result) {
    const respondentHighPriorityList = result.filter(
      r => r.assessmentPriority <= AssessmentConstants.REPORT_DIMENSION.NUM_INTEREST,
    );
    return AbilityRangeGenerator.generateRespondentInterestList(respondentHighPriorityList);
  }

  static generateRespondentInterestList(interstList) {
    if (interstList && interstList.length > 0) {
      return interstList.map((interest, index) => AbilityRangeGenerator.generateRespondentInterestTag(interest, index));
    }
    return null;
  }

  static generateRespondentInterestTag(interest, index) {
    return (
      <div
        key={index}
        className={`interest${
          interest.jobPriority <= AssessmentConstants.REPORT_DIMENSION.MATCH_PRIORITY ? ' interest--match' : ' '
        }`}
      >
        <b>{interest.assessmentPriority}</b>
        <span className="t-small">{interest.displayName}</span>
      </div>
    );
  }

  static generateInterestListForJobProfile(result) {
    const jplHighPriorityList = result.filter(r => r.jobPriority <= AssessmentConstants.REPORT_DIMENSION.NUM_INTEREST);
    return AbilityRangeGenerator.generateJobProfileInterestList(jplHighPriorityList);
  }

  static generateJobProfileInterestList(interestRawList) {
    const interestList = interestRawList.sort(
      (interestPrev, interestNext) => interestPrev.jobPriority - interestNext.jobPriority,
    );
    if (ArrayUtil.isNotEmpty(interestList)) {
      // eslint-disable-next-line max-len
      return interestList.map((interest, index) => AbilityRangeGenerator.generateJobProfileInterestTag(interest, index));
    }
    return null;
  }

  static generateJobProfileInterestTag(interest, index) {
    return (
      <div key={index} className="interest interest--benchmark">
        <b>{interest.jobPriority}</b>
        <span className="t-small">{interest.displayName}</span>
      </div>
    );
  }

  static mapOverallLabelToPercent(percent) {
    const roundedPercent = parseInt(percent, 10);
    const labelNum = Math.floor(roundedPercent / 10);
    let label = 0;

    if (labelNum >= AssessmentConstants.PERCENT_COLOR_MAP.LVL_10.FLOOR) {
      label = AssessmentConstants.PERCENT_COLOR_MAP.LVL_10.FLOOR;
    } else if (labelNum >= AssessmentConstants.PERCENT_COLOR_MAP.LVL_2.FLOOR) {
      label = labelNum;
    }
    const lvlLabel = `LVL_${label}`;
    return AssessmentConstants.PERCENT_COLOR_MAP[lvlLabel].COLOR;
  }
}
export default AssessmentUtil;
